import { CommonModule } from "@angular/common";
import { Component, inject, OnDestroy, OnInit, output, signal, WritableSignal } from "@angular/core";
import { FormArray, FormBuilder, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatSnackBarModule } from "@angular/material/snack-bar";
import { IndicationQuoteStepService } from "../../services/indicationQuoteStep.service";
import { forkJoin, map, Subscription } from 'rxjs';
import { PropertyService } from "../../services/property.service";
import { GetUserDetails } from "../../models/sessionStorage.model";
import { AuthService } from "../../auth/auth.service";
import { RequestQuoteService } from "../../services/requestQuote.service";
import { ToastService } from "../../services/toast.service";
import { EQBD_HIGH_BPP_RESTRICTED_DESCRIPTIONS, ToastType } from "../../enums/config.enum";
import { PolicyAdministrationService } from "../../services/policyAdministration.service";
import { SpinnerComponent } from "../utils/spinner/spinner.component";

@Component({
    selector: 'app-request-quote-referral-step',
    standalone: true,
    imports: [MatExpansionModule, MatSnackBarModule, CommonModule, FormsModule, ReactiveFormsModule, SpinnerComponent],
    templateUrl: './request-quote-referral-step.component.html',
    styleUrl: './request-quote-referral-step.component.scss',
})
export class RequestQuoteReferralStepComponent implements OnInit, OnDestroy{
    acceptAndIssueQuoteStep = signal(3);
    proceedToAcceptAndIssueQuoteStep = signal(false);
    additionalFactorInfo = signal([]);
    causeOfLossInfo = signal([]);
    PropertEnhancementInfo = signal([]);
    acceptAndIssueQuoteEmitter = output<number>();
    submitReferralEmitter = output<boolean>();
    saveIndicationHomeEmitter = output();
    storageData = signal<GetUserDetails | null>(null);
    indicationId = signal<string | null>('');
    quoteId = signal<string | null>('');
    branchId = signal<string | null>('');
    fb: FormBuilder = inject(FormBuilder);
    indicationQuoteService = inject(IndicationQuoteStepService);
    propertyService = inject(PropertyService);
    authService = inject(AuthService);
    requestQuoteService = inject(RequestQuoteService);
    policyService = inject(PolicyAdministrationService);
    toastService = inject(ToastService);
    ragIndicationColor: WritableSignal<string> = signal("#A99333");
    indicationQuoteDetails: any = signal(null);
    IndicationNo = signal<string>('Indication No');
    IndicationDate = signal<string>('Indication Date');
    isLoading = signal(false);
    eqbdOccupancyDetails = signal<any>(null);
    eqbdAdditionalPremium = signal<number>(50);
    isEqbdDisabledDueToDeclineRule = signal<boolean>(false);
    isEqbdDisabledDueToHighBpp = signal<boolean>(false);
    isEqbdDisabledDueToUWDecision = signal<boolean>(false);
    isEqbdUnavailableForRisk = signal<boolean>(false);
    indicationCoverageDataSubs = new Subscription();
    quoteSummaryForm = this.fb.group({
        address: this.fb.array([]),
        aopDeductibleAmount: [""],
        windDeductibleFlag:[0],
        windDeductibleFactor: [""],
        aopDeductibleDescription:[""],
        windDeductibleDescription:[""],
        causeOfLoss: [""],
        waterDamageAmount: [""],
        vandalismAmount: [""],
        vandalismDescription:[""],
        waterDamageDescription:[""],
        oAndLCoverageAmount: [""],
        propertyExtCoverageType: [""],
        totalInsuredValue:[""],
        targetPremium: [""],
        triaPremium: [""],
        indicationNo: [""],
        indicationDate: [""],
        inspectionFee:[""],
        inceptionDate:[""]
    });

    ngOnInit(): void {
        this.authService.waitForSessionCachesRestored().subscribe(() => {
            this.initializeAfterCacheHydration();
        });
    }

    private initializeAfterCacheHydration(): void {
        this.loadEQBDOccupancyDetails();
        this.loadEQBDReferralDetails();
        const isPolicyType = this.authService.isPolicyType();
        if(isPolicyType){
            this.calculatePolicyInspectionFees();
            this.IndicationNo.set("Policy No")
            this.IndicationDate.set("Policy Bound Date")
        }else{
            const storageData = this.authService.getUserDetailsFromStorage();
            this.storageData.set(storageData);
            this.isIndicationType() ? this.indicationId.set(storageData?.indicationId) : this.quoteId.set(storageData.quoteId);
            if(this.isIndicationType()){
                this.IndicationNo.set("Indication No")
                this.IndicationDate.set("Indication Saved Date")
            }else{
                this.IndicationNo.set("Quote No")
                this.IndicationDate.set("Quote Issued Date")
            }
            this.branchId.set(storageData?.branchId);

            const indicationQuoteId = this.isIndicationType() ? this.indicationId() : this.quoteId();
            const branchId = this.branchId();
            const userRole = this.authService.getUserDetailsFromStorage()?.userRole;
            if(userRole && Number(userRole) === 2){
                this.calculateIndicationQuoteFees(); // call only when UWAdmin
            }else{
                if(indicationQuoteId && branchId){
                    // this.getIndicationSummaryDetails(Number(indicationQuoteId), Number(branchId));
                    this.calculateIndicationQuoteFees()
                };
            }
        }
        }

    private getEQBDContext(): { id: number; type: string } | null {
        const userDetails = this.authService.getUserDetailsFromStorage();
        if (!userDetails) {
            return null;
        }

        if (userDetails.isPolicyType && userDetails.policyId) {
            return { id: Number(userDetails.policyId), type: 'Policy' };
        }

        if (userDetails.quoteType === 'indication' && userDetails.indicationId) {
            return { id: Number(userDetails.indicationId), type: 'Indication' };
        }

        if (userDetails.quoteId) {
            return { id: Number(userDetails.quoteId), type: 'Quote' };
        }

        return null;
    }

    private loadEQBDOccupancyDetails(): void {
        const context = this.getEQBDContext();
        if (!context || !context.id) {
            return;
        }

        this.indicationQuoteService.GetEQBDOccupanyDetails(context.id, context.type).subscribe({
            next: (response: any) => {
                this.eqbdOccupancyDetails.set(response.data.eqbdOccupancyDetails ?? null);
                this.setEqbdDetails(response?.data ?? response);
            },
            error: () => {
                this.eqbdOccupancyDetails.set(null);
                this.eqbdAdditionalPremium.set(50);
                this.isEqbdDisabledDueToDeclineRule.set(false);
                this.isEqbdDisabledDueToHighBpp.set(false);
                this.updateEqbdAvailabilityState();
            }
        });
    }

    private setEqbdDetails(response: any): void {
        const items = Array.isArray(response?.eqbdOccupancyDetails)
            ? response.eqbdOccupancyDetails
            : (Array.isArray(response) ? response : []);

        const hasDeclineRule = items.some((item: any) => String(item?.rule || '').toLowerCase() === 'decline');
        const restrictedDescriptions = new Set(EQBD_HIGH_BPP_RESTRICTED_DESCRIPTIONS.map((desc) => desc.toLowerCase()));
        const hasRestrictedDescriptionWithHighBpp = items.some((item: any) => {
            const occupancyDescription = String(item?.occupancyDecription || item?.description || '').trim().toLowerCase();
            const bppLimit = Number(item?.bppLimit ?? 0);
            return restrictedDescriptions.has(occupancyDescription) && bppLimit > 499999;
        });

        const totalPremium = items.reduce((sum: number, item: any) => {
            const value = Number(item?.eqbdCalcPremium ?? 0);
            return sum + (isNaN(value) ? 0 : value);
        }, 0);

        this.isEqbdDisabledDueToDeclineRule.set(hasDeclineRule);
        this.isEqbdDisabledDueToHighBpp.set(hasRestrictedDescriptionWithHighBpp);
        this.eqbdAdditionalPremium.set(totalPremium < 50 ? 50 : totalPremium);
        this.updateEqbdAvailabilityState();
    }

    private updateEqbdAvailabilityState(): void {
        this.isEqbdUnavailableForRisk.set(
            this.isEqbdDisabledDueToDeclineRule()
            || this.isEqbdDisabledDueToHighBpp()
            || this.isEqbdDisabledDueToUWDecision()
        );
    }

    private loadEQBDReferralDetails(): void {
        const context = this.getEQBDContext();
        if (!context || !context.id || context.type === 'Policy') {
            return;
        }
        const type = context.type.toLowerCase();
        this.indicationQuoteService.GetEQBDReferralDetails(context.id, type).subscribe({
            next: (response: any) => {
                const records = Array.isArray(response?.data)
                    ? response.data
                    : (Array.isArray(response) ? response : []);
                const currentVersionNo = Number(sessionStorage.getItem("versionNo"));
                const currentVersionRecord = records.find(
                    (item: any) => Number(item?.versionNo) === currentVersionNo
                );
                const availabilityFlag = currentVersionRecord?.isEQBDAvailabe;
                const isDisabledByUW = availabilityFlag !== null
                    && availabilityFlag !== undefined
                    && availabilityFlag !== ''
                    && Number(availabilityFlag) === 0;

                this.isEqbdDisabledDueToUWDecision.set(isDisabledByUW);
                this.updateEqbdAvailabilityState();
            },
            error: () => {
                this.isEqbdDisabledDueToUWDecision.set(false);
                this.updateEqbdAvailabilityState();
            }
        });
    }

    calculateIndicationQuoteFees(){
        this.isIndicationType() ? this.calculateIndicationInspectionFees() : this.calculateQuoteInspectionFees()
    }

    calculateIndicationInspectionFees(){
        this.isLoading.set(true);
        const indicationId = Number(this.authService.getUserDetailsFromStorage()?.indicationId);
        this.indicationQuoteService.IndicationInspectionFees(indicationId).subscribe({
            next: (response: any) => {
              if (response) {
                this.fetchCoverageDetails()
              }
            },        
            error: (error: any) => {
                 this.isLoading.set(false);
            },
        });
    }

    calculateQuoteInspectionFees(){
        this.isLoading.set(true);
        const quoteId = Number(this.authService.getUserDetailsFromStorage()?.quoteId);
        this.indicationQuoteService.QuoteInspectionFees(quoteId).subscribe({
            next: (response: any) => {
              if (response) {
                this.fetchCoverageDetails()
              }
            },        
            error: (error: any) => {
                this.isLoading.set(false);
            },
        });
    }

    calculatePolicyInspectionFees(){
        this.isLoading.set(true);
        const policyId = Number(this.authService.getUserDetailsFromStorage()?.policyId)        
        this.indicationQuoteService.PolicyInspectionFees(policyId).subscribe({
            next: (response: any) => {
              if (response) {

                this.getPolicyCoverageSummaryDetails();
            }
            },        
            error: (error: any) => {
                this.isLoading.set(false);
            },
        });
    }
    

    fetchCoverageDetails() { 
        
        const indicationId = this.authService.getUserDetailsFromStorage()?.indicationId;
        const quoteId = this.authService.getUserDetailsFromStorage()?.quoteId;
        const branchId = this.authService.getUserDetailsFromStorage()?.branchId;
        this.isIndicationType() ? this.getIndicationSummaryDetails(Number(indicationId), Number(branchId)) :
        this.getQuoteSummaryDetails(Number(quoteId), Number(branchId));
    }

    getIndicationSummaryDetails(indicationId: number, branchId: number) {
        forkJoin([
            this.propertyService.getCoverageFactors(),
            this.propertyService.getCauseOfLossOptions(),            
            this.indicationQuoteService.fetchIndicationCoverageSummaryDetails(indicationId, branchId),            
            this.propertyService.getPropertyEnhancementCoverages(),
          ]).pipe(
            map(([addnlCoverageFactor, causeOfLossOptions, indicationSummaryDetails, propExtensionData]) => {
              return {addnlCoverageFactor, causeOfLossOptions, indicationSummaryDetails, propExtensionData};
            })
          ).subscribe({
            next: (response: any) => {
                const {addnlCoverageFactor, causeOfLossOptions, indicationSummaryDetails,propExtensionData} = response;
                if(indicationSummaryDetails && indicationSummaryDetails.result && addnlCoverageFactor && causeOfLossOptions && propExtensionData){
                    this.additionalFactorInfo.set(addnlCoverageFactor);
                    this.causeOfLossInfo.set(causeOfLossOptions);
                    this.PropertEnhancementInfo.set(propExtensionData);
                    this.bindRequestQuoteSummaryData(indicationSummaryDetails.result);
                    const status = sessionStorage.getItem("statusRagCall");
                    const statusAvailable = sessionStorage.getItem("statusAvailable");                  
                    const RagCall = sessionStorage.getItem("RagCall");
                     if ( statusAvailable !== "true" || RagCall === "true"){
                        this.fetchIndicationRagdetails();
                    }else{
                        this.getRagData();
                    }
                           
                    this.proceedToAcceptAndIssueQuoteStep.set(true);
                }  
            },
            error: (error) => {
                this.isLoading.set(false);
                console.log("error", error);
                this.toastService.addNotification({message: "System error. Failed to retrieve data. Please exit and contact help@AbacusAU.com.", type: ToastType.Error}); // error notification
            }
        })
    }

    getQuoteSummaryDetails(quoteId: number, branchId: number) {
        forkJoin([
            this.propertyService.getCoverageFactors(),
            this.propertyService.getCauseOfLossOptions(),
            this.indicationQuoteService.fetchQuoteCoverageSummaryDetails(quoteId, branchId),
            this.propertyService.getPropertyEnhancementCoverages(),
          ]).pipe(
            map(([addnlCoverageFactor, causeOfLossOptions, quoteSummaryDetails,propExtensionData]) => {
              return {addnlCoverageFactor, causeOfLossOptions, quoteSummaryDetails,propExtensionData};
            })
          ).subscribe({
            next: (response: any) => {
                const {addnlCoverageFactor, causeOfLossOptions, quoteSummaryDetails,propExtensionData} = response;
                if(quoteSummaryDetails && quoteSummaryDetails.result && addnlCoverageFactor && causeOfLossOptions && propExtensionData){
                    this.additionalFactorInfo.set(addnlCoverageFactor);
                    this.causeOfLossInfo.set(causeOfLossOptions);
                    this.PropertEnhancementInfo.set(propExtensionData);
                    this.bindQuoteCoverageSummaryData(quoteSummaryDetails.result);
                    const statusAvailable = sessionStorage.getItem("statusAvailable");                  
                    const RagCall = sessionStorage.getItem("RagCall");
                     if ( statusAvailable !== "true" || RagCall === "true"){
                        this.fetchQuoteRagdetails();
                    }else{
                        this.getRagData();
                    }
                }  
            },
            error: (error) => {
                this.isLoading.set(false);
                console.log("error", error);
            }
        })
    }

    getPolicyCoverageSummaryDetails() {
        forkJoin([
            this.propertyService.getCoverageFactors(),
            this.propertyService.getCauseOfLossOptions(),
            this.policyService.getPolicyCoverageSummary(Number(this.authService.getUserDetailsFromStorage()?.policyId), Number(this.authService.getUserDetailsFromStorage()?.branchId)),
            this.propertyService.getPropertyEnhancementCoverages(),
          ]).pipe(
            map(([addnlCoverageFactor, causeOfLossOptions, policySummaryDetails, propExtensionData]) => {
              return {addnlCoverageFactor, causeOfLossOptions, policySummaryDetails, propExtensionData};
            })
          ).subscribe({
            next: (response: any) => {
                const {addnlCoverageFactor, causeOfLossOptions, policySummaryDetails, propExtensionData} = response;
                if(policySummaryDetails && policySummaryDetails.result && addnlCoverageFactor && causeOfLossOptions && propExtensionData){
                    this.additionalFactorInfo.set(addnlCoverageFactor);
                    this.causeOfLossInfo.set(causeOfLossOptions);
                    this.PropertEnhancementInfo.set(propExtensionData);
                    this.bindPolicySummaryData(policySummaryDetails.result);
                }  
            },
            error: (error) => {
                this.isLoading.set(false);
                console.log("error", error);
                this.toastService.addNotification({message: "System error. Failed to retrieve data. Please exit and contact help@AbacusAU.com.", type: ToastType.Error}); // error notification
            }
        });
    }

    bindRequestQuoteSummaryData(summaryDetails: any) {
        
        this.setAddress(summaryDetails.addresses, summaryDetails.buildings);
        this.quoteSummaryForm.patchValue({
            aopDeductibleAmount: summaryDetails.aopDeductibleAmount,
            windDeductibleFlag:summaryDetails.windDeductibleFlag,
            windDeductibleFactor: summaryDetails.windDeductibleFactor,
            windDeductibleDescription:summaryDetails.windDeductibleDescription,
            aopDeductibleDescription:summaryDetails.aopDeductibleDescription,
            causeOfLoss: this.getCauseOfLossData(summaryDetails.causeOfLoss),
            waterDamageAmount: summaryDetails.waterDamageAmount,
            vandalismAmount: summaryDetails.vandalismAmount,
            waterDamageDescription:summaryDetails.waterDamageDescription,            
            vandalismDescription:summaryDetails.vandalismDescription,
            oAndLCoverageAmount: summaryDetails.oAndLCoverageAmount,
            propertyExtCoverageType: this.getEnhancementData(summaryDetails.propertyExtCoverageType),
            totalInsuredValue:summaryDetails.totalInsuredValue,
            targetPremium: summaryDetails.targetPremium,
            triaPremium: summaryDetails.triaPremium,
            indicationNo: summaryDetails?.indicationNo,
            inspectionFee:summaryDetails?.inspectionFee,
            indicationDate: summaryDetails?.indicationDate,
            inceptionDate: summaryDetails?.policyStartDate
        });
         this.isLoading.set(false);
    }

    bindQuoteCoverageSummaryData(response: any) {
        
        if (!Array.isArray(response) || response.length === 0) {
            console.error('Response is empty or invalid');
            return;
        }
    
        // Find the object with the highest versionNo
        const highestVersionData = response.reduce((prev, current) => {
            return current.versionNo > prev.versionNo ? current : prev;
        });
    
        // Ensure addresses and buildings exist
        if (!highestVersionData.addresses || !highestVersionData.buildings) {
            console.error('Addresses or buildings data is missing');
            return;
        }

        this.indicationQuoteService.setSelectedQuoteVersion(highestVersionData) // Set selected quote version

        // Set addresses and bind data to form
        this.setQuoteAddress(highestVersionData.addresses, highestVersionData.buildings);
    
        this.quoteSummaryForm.patchValue({
            aopDeductibleAmount: highestVersionData.aopDeductibleAmount,
            windDeductibleFactor: highestVersionData.windDeductibleFactor,
            causeOfLoss: this.getCauseOfLossData(highestVersionData.causeOfLoss),
            waterDamageAmount: highestVersionData.waterDamageAmount,
            windDeductibleDescription:highestVersionData.windDeductibleDescription,
            aopDeductibleDescription:highestVersionData.aopDeductibleDescription,
            vandalismAmount: highestVersionData.vandalismAmount,
            waterDamageDescription:highestVersionData.waterDamageDescription,            
            vandalismDescription:highestVersionData.vandalismDescription,
            oAndLCoverageAmount: highestVersionData.oAndLCoverageAmount,
            propertyExtCoverageType: this.getEnhancementData(highestVersionData.propertyExtCoverageType),
            totalInsuredValue:highestVersionData?.totalInsuredValue,
            targetPremium: highestVersionData.targetPremium,
            triaPremium: highestVersionData?.triaPremium,
            inspectionFee:highestVersionData.inspectionFee,
            indicationNo: highestVersionData?.quoteNo,
            indicationDate: highestVersionData?.quoteDate,
            inceptionDate: highestVersionData?.policyStartDate

        });
         this.isLoading.set(false);
    }

    bindQuoteSummaryData(summaryDetails: any) {
        this.setQuoteAddress(summaryDetails.addresses, summaryDetails.buildings);
        this.quoteSummaryForm.patchValue({
            aopDeductibleAmount: summaryDetails.aopDeductibleAmount,
            windDeductibleFlag:summaryDetails.windDeductibleFlag,
            windDeductibleFactor: summaryDetails.windDeductibleFactor,
            windDeductibleDescription:summaryDetails.windDeductibleDescription,
            aopDeductibleDescription:summaryDetails.aopDeductibleDescription,
            causeOfLoss: this.getCauseOfLossData(summaryDetails.causeOfLoss),
            waterDamageAmount: summaryDetails.waterDamageAmount,
            vandalismAmount: summaryDetails.vandalismAmount,
            waterDamageDescription:summaryDetails.waterDamageDescription,            
            vandalismDescription:summaryDetails.vandalismDescription,
            oAndLCoverageAmount: summaryDetails.oAndLCoverageAmount,
            propertyExtCoverageType: this.getEnhancementData(summaryDetails.propertyExtCoverageType),
            totalInsuredValue:summaryDetails.totalInsuredValue,
            targetPremium: summaryDetails.targetPremium,
            triaPremium: summaryDetails.triaPremium,
            inspectionFee:summaryDetails.inspectionFee,
            indicationNo: summaryDetails?.quoteNo,
            indicationDate: summaryDetails?.quoteDate,
            inceptionDate: summaryDetails?.policyStartDate

        });
         this.isLoading.set(false);
    }

    bindPolicySummaryData(summaryDetails: any) {
        this.setPolicyAddress(summaryDetails.addresses, summaryDetails.buildings);
        this.quoteSummaryForm.patchValue({
            aopDeductibleAmount: summaryDetails.aopDeductibleAmount,
            windDeductibleFlag:summaryDetails.windDeductibleFlag,
            windDeductibleFactor: summaryDetails.windDeductibleFactor,
            windDeductibleDescription:summaryDetails?.windDeductibleDescription,
            aopDeductibleDescription:summaryDetails?.aopDeductibleDescription,
            causeOfLoss: this.getCauseOfLossData(summaryDetails.causeOfLoss),
            waterDamageAmount: summaryDetails.waterDamageAmount,
            vandalismAmount: summaryDetails.vandalismAmount,
            waterDamageDescription:summaryDetails?.waterDamageDescription,            
            vandalismDescription:summaryDetails?.vandalismDescription,
            oAndLCoverageAmount: summaryDetails.oAndLCoverageAmount,
            propertyExtCoverageType: this.getEnhancementData(summaryDetails.propertyExtCoverageType),
            totalInsuredValue:summaryDetails.totalInsuredValue,
            targetPremium: summaryDetails.targetPremium,
            triaPremium: summaryDetails.triaPremium,
            inspectionFee:summaryDetails.inspectionFee,
            indicationNo: summaryDetails?.policyNo,
            indicationDate: summaryDetails?.policyDate,
            inceptionDate: summaryDetails?.policyStartDate

        });
        this.isLoading.set(false);
    }

    getAddressForm(): FormArray {
        return this.quoteSummaryForm.get("address") as FormArray;
    }

    getBuildingForm(indexNumber: number): FormArray{
        return this.getAddressForm().at(indexNumber).get("buildings") as FormArray;
    }

    setAddress(addressData: any[], buildingData: any[]){
        (addressData ?? []).forEach((address) => {
            const addressGroup = this.fb.group({
                streetNum:address.streetNum,
                streetName: address.streetName,
                unitNum:address.unitNum,
                occupancy: address.occupany,
                city: address.city,
                state: address.state,
                county: address.county,
                zipcode: address.zipcode, 
                businessIncomeLimit: address.businessIncomeLimit,
                locationTIV: address.locationTIV,
                buildings: this.fb.array([])
            });
            this.setBuilding(addressGroup.get('buildings') as FormArray, address.addressId, buildingData);
            this.getAddressForm().push(addressGroup);
        });
    }

    setBuilding(buildingsFormArray: FormArray, addressId: number, buildingsData: any[]) {
        const matchingBuilding: any[] = (buildingsData ?? []).filter(building => building.indicationAddressId === addressId);

        matchingBuilding.forEach((building) => {
            buildingsFormArray.push(this.fb.group({
                buildingName: building.buildingName,
                buildingAge: building.buildingAge,
                buildingLimit: building.buildingLimit,
                businessPropertyLimit: building.businessPropertyLimit,
                additionalCoverageLimit: this.getAdditionalCoverageFactorData(building.otherStructureFactorId),
                oandLFlag: building.oandLFlag,
                oandLDiscription: building?.oandLDiscription
            }));
        });
    }

    setQuoteAddress(addressData: any[], buildingData: any[]){
        (addressData ?? []).forEach((address) => {
            const addressGroup = this.fb.group({
                streetNum:address.streetNum,
                streetName: address.streetName,
                unitNum:address.unitNum,
                occupancy: address.occupany,
                city: address.city,
                state: address.state,
                county: address.county,
                zipcode: address.zipcode, 
                businessIncomeLimit: address.businessIncomeLimit,
                locationTIV: address.locationTIV,
                buildings: this.fb.array([])
            });
            this.setQuoteBuilding(addressGroup.get('buildings') as FormArray, address.addressId, buildingData);
            this.getAddressForm().push(addressGroup);
        });
    }

    setPolicyAddress(addressData: any[], buildingData: any[]){
        (addressData ?? []).forEach((address) => {
            const addressGroup = this.fb.group({
                streetNum:address.streetNum,
                streetName: address.streetName,
                unitNum:address.unitNum,
                occupancy: address.occupany,
                city: address.city,
                state: address.state,
                county: address.county,
                zipcode: address.zipcode, 
                businessIncomeLimit: address.businessIncomeLimit,
                locationTIV: address.locationTIV,
                buildings: this.fb.array([])
            });
            this.setPolicyBuilding(addressGroup.get('buildings') as FormArray, address.addressId, buildingData);
            this.getAddressForm().push(addressGroup);
        });
    }

    setQuoteBuilding(buildingsFormArray: FormArray, addressId: number, buildingsData: any[]) {
        const matchingBuilding: any[] = (buildingsData ?? []).filter(building => building.quoteAddressId === addressId);
        matchingBuilding.forEach((building) => {
            buildingsFormArray.push(this.fb.group({
                buildingName: building.buildingName,
                buildingAge: building.buildingAge,
                buildingLimit: building.buildingLimit,
                businessPropertyLimit: building.businessPropertyLimit,
                additionalCoverageLimit: this.getAdditionalCoverageFactorData(building.otherStructureFactorId),
                oandLFlag: building.oandLFlag,
                oandLDiscription: building?.oandLDiscription
            }));
        });
    }

    setPolicyBuilding(buildingsFormArray: FormArray, addressId: number, buildingsData: any[]) {
        const matchingBuilding: any[] = (buildingsData ?? []).filter(building => building.policyAddressId === addressId);
        matchingBuilding.forEach((building) => {
            buildingsFormArray.push(this.fb.group({
                buildingName: building.buildingName,
                buildingAge: building.buildingAge,
                buildingLimit: building.buildingLimit,
                businessPropertyLimit: building.businessPropertyLimit,
                additionalCoverageLimit: this.getAdditionalCoverageFactorData(building.otherStructureFactorId),
                oandLFlag: building.oandLFlag,
                oandLDiscription: building?.oandLDiscription
            }));
        });
    }


    getEnhancementData(extensionFactorId: number) : string | null {
        
        
        if(extensionFactorId) {
            const getPropertyData: any[] = this.PropertEnhancementInfo().filter((propdata: any) => Number(propdata.extensionFactorId) === extensionFactorId);
            
            if(getPropertyData.length > 0) {
                return getPropertyData[0]?.description;
            }else{
                return 'No Enhancement Selected';
            }
        }
        return 'No Enhancement Selected';
    }

    getCauseOfLossData(gencodeId: number) : string | null {
        if(gencodeId) {
            const getCauseOfLossData: any[] = this.causeOfLossInfo().filter((cofloss: any) => Number(cofloss.gencodeId) === gencodeId);
            if(getCauseOfLossData.length > 0) {
                return getCauseOfLossData[0]?.description;
            }else{
                return null;
            }
        }
        return null;
    }

    getAdditionalCoverageFactorData(factorId: number) : string | null {
        if(factorId){
            const getFactorData: any[] = this.additionalFactorInfo().filter((factor: any) => Number(factor.factorId) === factorId);
            if(getFactorData.length > 0) {
                return getFactorData[0]?.factorDescription;
            }else{
                return null;
            }
        }
        return null;
    }

    isIndicationType(): boolean {
        const quoteType = this.storageData()?.quoteType;
        return quoteType === "indication";
    }

    updateRagIndicationColor(feedBackType: string = "") {
        switch(feedBackType){
          case "GreenRange":
            this.ragIndicationColor.set("#238823");
            break;
          case "AmberRange":
            this.ragIndicationColor.set("#FFBF00");
            break;
          case "RedRange":
            this.ragIndicationColor.set("#FF0000");
            break;
          case "ReferralRedLow":
            this.ragIndicationColor.set("#FF0000");
            break;
          case "ReferralRedHigh":
            this.ragIndicationColor.set("#238823");
            break;
                  
          default:
            this.ragIndicationColor.set('#A99333')  // default gold color
        }
      }

    setIndicationQuoteRagDetails(response: any){
        this.updateRagIndicationColor(response.metricName);
      }


       getRagData() {
            if (Number(this.authService.getUserDetailsFromStorage()?.indicationId)) {
            let type: string;
            if (this.authService?.getUserDetailsFromStorage()?.isPolicyType) {
                type = 'Policy';
            } else if (this.isIndicationType()) { 
                type = 'Indication';
            } else {
                type = 'Quote';
            }

            this.indicationQuoteService.getPolicyRAG(Number(this.authService.getUserDetailsFromStorage()?.indicationId), type).subscribe(
                response => {
                this.setIndicationQuoteRagDetails(response);   
                this.indicationQuoteDetails.set(response);             
                },
                error => {
                }
            );
            }
        }

    fetchIndicationRagdetails(){     
          this.indicationQuoteService.getIndicationRating(Number(this.authService.getUserDetailsFromStorage()?.indicationId), Number(this.authService.getUserDetailsFromStorage()?.userDetails?.userId)).subscribe({
          next: (response: any) => {
            if(response){
              this.setIndicationQuoteRagDetails(response);                    
              this.indicationQuoteDetails.set(response);

            }
          },
          error: (error: any) => {
            console.log("error", error);
          }
        })
      }

      fetchQuoteRagdetails(){
          this.indicationQuoteService.getQuoteRating(Number(this.authService.getUserDetailsFromStorage()?.quoteId), Number(this.authService.getUserDetailsFromStorage()?.userDetails?.userId)).subscribe({
          next: (response: any) => {
            if(response){
              this.setIndicationQuoteRagDetails(response);
              this.indicationQuoteDetails.set(response);

            }
          },
          error: (error: any) => {
            console.log("error", error);
          }
        })
      }

    ngOnDestroy(): void {
        this.indicationCoverageDataSubs.unsubscribe();
    }
}
